import { useEffect, RefObject } from 'react';

interface UseAnnotationClickOutsideOptions {
  descriptionBoxRef: RefObject<HTMLDivElement>;
  activeAnnotation: any;
  onClose: () => void;
  imageModalOpened?: boolean;
  productModalOpened?: boolean;
}

/** Minimum pointer movement (px) before a model-viewer interaction counts as orbit/pan drag. */
const MODEL_VIEWER_DRAG_THRESHOLD_PX = 8;

function isModelViewerPointerTarget(event: PointerEvent): boolean {
  return event.composedPath().some((node) => node instanceof Element && node.tagName === 'MODEL-VIEWER');
}

function isHotspotPointerTarget(event: PointerEvent): boolean {
  return event.composedPath().some((node) => {
    if (!(node instanceof HTMLButtonElement)) return false;
    const slot = node.getAttribute('slot');
    return Boolean(slot?.startsWith('hotspot-'));
  });
}

function shouldCloseFromTarget(
  target: HTMLElement,
  descriptionBoxRef: RefObject<HTMLDivElement>
): boolean {
  if (target.closest('[data-annotation-keep-open]')) {
    return false;
  }

  return Boolean(
    descriptionBoxRef.current &&
      !descriptionBoxRef.current.contains(target) &&
      !target.closest('button[slot^="hotspot-"]')
  );
}

function shouldDeferCloseForModal(
  target: HTMLElement,
  imageModalOpened: boolean,
  productModalOpened: boolean
): boolean {
  const modalElement =
    target.closest('.mantine-Modal-root') || target.closest('.mantine-Modal-inner');
  if (!modalElement) return false;

  if (
    target.closest('[data-annotation-link-gate]') ||
    target.closest('.annotation-link-gate-modal')
  ) {
    return true;
  }
  if (imageModalOpened || productModalOpened) {
    return true;
  }
  return false;
}

/**
 * Closes the annotation info box when clicking outside it.
 * Orbit/pan drags on the model canvas do not close; a tap on empty model surface does.
 */
export function useAnnotationClickOutside({
  descriptionBoxRef,
  activeAnnotation,
  onClose,
  imageModalOpened = false,
  productModalOpened = false,
}: UseAnnotationClickOutsideOptions) {
  useEffect(() => {
    let viewerPointerId: number | null = null;
    let pointerStartX = 0;
    let pointerStartY = 0;
    let viewerDragged = false;

    const resetViewerPointer = () => {
      viewerPointerId = null;
      viewerDragged = false;
    };

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as HTMLElement;

      if (isHotspotPointerTarget(event)) {
        resetViewerPointer();
        return;
      }

      if (isModelViewerPointerTarget(event)) {
        viewerPointerId = event.pointerId;
        pointerStartX = event.clientX;
        pointerStartY = event.clientY;
        viewerDragged = false;
        return;
      }

      resetViewerPointer();

      if (shouldDeferCloseForModal(target, imageModalOpened, productModalOpened)) {
        return;
      }

      if (shouldCloseFromTarget(target, descriptionBoxRef)) {
        onClose();
      }
    };

    const handlePointerMove = (event: PointerEvent) => {
      if (viewerPointerId == null || event.pointerId !== viewerPointerId) return;

      const dx = event.clientX - pointerStartX;
      const dy = event.clientY - pointerStartY;
      if (Math.hypot(dx, dy) >= MODEL_VIEWER_DRAG_THRESHOLD_PX) {
        viewerDragged = true;
      }
    };

    const handlePointerUp = (event: PointerEvent) => {
      if (viewerPointerId == null || event.pointerId !== viewerPointerId) return;

      const target = event.target as HTMLElement;
      const wasTapOnViewer = isModelViewerPointerTarget(event);
      const tappedHotspot = isHotspotPointerTarget(event);
      const dragged = viewerDragged;

      resetViewerPointer();

      if (!wasTapOnViewer || dragged || tappedHotspot) {
        return;
      }

      if (shouldDeferCloseForModal(target, imageModalOpened, productModalOpened)) {
        return;
      }

      if (shouldCloseFromTarget(target, descriptionBoxRef)) {
        onClose();
      }
    };

    if (activeAnnotation) {
      document.addEventListener('pointerdown', handlePointerDown);
      document.addEventListener('pointermove', handlePointerMove);
      document.addEventListener('pointerup', handlePointerUp);
      document.addEventListener('pointercancel', resetViewerPointer);
    }

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('pointermove', handlePointerMove);
      document.removeEventListener('pointerup', handlePointerUp);
      document.removeEventListener('pointercancel', resetViewerPointer);
      resetViewerPointer();
    };
  }, [activeAnnotation, imageModalOpened, productModalOpened, descriptionBoxRef, onClose]);
}
